/**
 * "What does that word mean?"
 *
 * [Tap-to-hint](../../../docs/REVIEW.md#tap-to-hint): tap any English word,
 * anywhere, and hear it and see what it means. The most-used feature in
 * Duolingo, and it matters more here than there — the whole interface is
 * English ([D-068](../../../docs/DECISIONS.md#d-068)), so a child who does not
 * know a word in an **instruction** has no other way to ask.
 *
 * **Free, unlimited, and not counted.** No score penalty, no hint counter, no
 * limit, and it works in hard mode: hard mode removes *scaffolding* — the word
 * banks and the multiple choice that do the work for you — never *help*. A hint
 * tells a child what a word means so they can do the work themselves, and
 * charging for that only teaches them not to ask.
 *
 * It is also **not recorded**. The design says a repeatedly-tapped word is a
 * useful signal for the adaptive engine, and it is, but it is not built:
 * shipping the counter before the screen would be recording a child's
 * confusions before anything could act on them, and the whole point of the
 * feature is that asking costs nothing.
 *
 * **Why a context rather than props.** "Every screen" is the requirement, so the
 * alternative is threading a vocabulary and a callback through every component
 * that renders a sentence — including the exercise widgets, which are three
 * levels down. The provider also lets the lookups be **batched**: a screen with
 * six lines on it asks once, on the way in, rather than six times.
 */

import '@capytalk/tokens/blocks/hint.css';

import { lookupForm, pieces, wordsIn } from '@capytalk/core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Client } from './api.js';
import { assetUrl, hush, say } from './audio.js';
import { learnt } from './phonemes.js';

/** One word, as `learner.hints` describes it. */
export interface Known {
  readonly word: string;
  readonly meaning: string;
  readonly ipa: string;
  readonly audioRef: string;
  readonly imageRef: string | null;
}

/** Where a child already met a word ([D-135](../../../docs/DECISIONS.md#d-135)). */
export interface Heard {
  readonly sentence: string;
  readonly where: string;
}

interface Hints {
  /** Lookup form → what is known about it. Absent means "not a word we teach". */
  readonly vocabulary: ReadonlyMap<string, Known>;
  /** Ask the server about the words in a line, once each. */
  readonly learn: (line: string) => void;
  readonly ask: (known: Known) => void;
  /** Which exercise is on screen, so its own sentence is never offered back. */
  readonly onScreen: (exerciseId: string | null) => void;
}

/**
 * The default: no vocabulary and nothing happens.
 *
 * So a component that renders a sentence works outside a provider — in a test
 * of that component alone, say — with every word as plain text. Tap-to-hint is
 * help, and help that is missing must never be an error.
 */
const Nothing: Hints = {
  vocabulary: new Map(),
  learn: () => undefined,
  ask: () => undefined,
  onScreen: () => undefined,
};

const HintContext = createContext<Hints>(Nothing);

export function HintProvider({
  client,
  children,
}: {
  readonly client: Client;
  readonly children: React.ReactNode;
}) {
  const [vocabulary, setVocabulary] = useState<ReadonlyMap<string, Known>>(new Map());
  const [asked, setAsked] = useState<Known | null>(null);
  const [heard, setHeard] = useState<Heard | null>(null);
  const showing = useRef<string | null>(null);

  /** Every word already asked about, answered or not, so nothing is asked twice. */
  const seen = useRef(new Set<string>());
  /** Words waiting to go out, and the timer that will send them. */
  const queued = useRef(new Set<string>());
  const flushing = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    flushing.current = null;
    const words = [...queued.current];
    queued.current.clear();
    if (words.length === 0) return;

    // `Promise.resolve().then(...)` rather than calling straight out, because
    // the call can *throw* rather than reject — a client whose router has no
    // `learner.hints` at all, which is every deploy where the server is older
    // than the app (D-066). A synchronous throw inside a timer is not caught by
    // a `.catch` on the promise it never returned, and it takes the tab down.
    Promise.resolve()
      .then(() => client.learner.hints.query({ words }))
      .then((answer) => {
        setVocabulary((was) => {
          const next = new Map(was);
          for (const word of answer.words) next.set(word.word.toLowerCase(), word);
          return next;
        });
        // The same answer carries how each word is pronounced, so Sneakers'
        // mouth gets it free ([D-081](../../../docs/DECISIONS.md#d-081)). He
        // says these words too, and by the time he does the app has them.
        for (const word of answer.words) learnt(word.word, word.ipa);
      })
      .catch(() => {
        // A hint that will not load leaves the word as plain text. It is help,
        // and a child mid-question must not get an error screen for asking.
        // The words stay in `seen`, so a broken connection does not turn every
        // rendered sentence into a retry loop.
      });
  }, [client]);

  const learn = useCallback(
    (line: string) => {
      let added = false;
      for (const word of wordsIn(line)) {
        if (seen.current.has(word)) continue;
        seen.current.add(word);
        queued.current.add(word);
        added = true;
      }
      // One request per screen rather than one per line: everything that mounts
      // in the same tick lands in the same batch.
      if (added && flushing.current === null) flushing.current = setTimeout(flush, 0);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (flushing.current !== null) clearTimeout(flushing.current);
    },
    [],
  );

  const onScreen = useCallback((exerciseId: string | null) => {
    showing.current = exerciseId;
  }, []);

  /*
    The sentence they heard it in, asked for when the card opens. It arrives a
    moment after the card does, and a card without it is still a whole card —
    so a slow or failed answer simply leaves the space empty.
  */
  useEffect(() => {
    setHeard(null);
    if (asked === null) return;
    let live = true;
    const exerciseId = showing.current;
    Promise.resolve()
      .then(() =>
        client.learner.heardIn.query({
          word: asked.word,
          ...(exerciseId === null ? {} : { exerciseId }),
        }),
      )
      .then((answer) => {
        if (live) setHeard(answer);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [asked, client]);

  const value = useMemo<Hints>(
    () => ({ vocabulary, learn, ask: setAsked, onScreen }),
    [vocabulary, learn, onScreen],
  );

  return (
    <HintContext.Provider value={value}>
      {children}
      {asked !== null && (
        <HintCard
          known={asked}
          heard={heard}
          onClose={() => {
            setAsked(null);
          }}
        />
      )}
    </HintContext.Provider>
  );
}

/**
 * The card, over a dimmed screen.
 *
 * Over the lesson rather than replacing it, because a child asking what a word
 * means is in the middle of a question and has to be able to still see it.
 */
export function HintCard({
  known,
  heard = null,
  onClose,
}: {
  readonly known: Known;
  readonly heard?: Heard | null;
  readonly onClose: () => void;
}) {
  // Said on arrival, like Sneakers: hearing the word is half of what the card is
  // for, and a child who cannot read the meaning still gets the sound.
  useEffect(() => {
    void say(known.audioRef, known.word);
    return hush;
  }, [known]);

  // Escape closes it. A grown-up will try it, and a child on a tablet with a
  // case keyboard will hit it by accident and expect it to work.
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', key);
    return () => {
      globalThis.removeEventListener('keydown', key);
    };
  }, [onClose]);

  return (
    <>
      {/*
        The dimmer is tappable to dismiss, and hidden from assistive technology
        because the close control below is the real one — announcing a
        full-screen unlabelled button would be noise.
      */}
      <button
        className="dim"
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{ border: 0, padding: 0 }}
        onClick={onClose}
      />
      <div className="pop" role="dialog" aria-modal="true" aria-label={known.word}>
        <div className="between">
          <h2>{known.word}</h2>
          <button
            className="x"
            type="button"
            aria-label="Close"
            style={{ background: 'none', border: 0, font: 'inherit' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="row" style={{ gap: 12, marginTop: 10 }}>
          {known.imageRef !== null && (
            <img
              src={assetUrl(known.imageRef)}
              alt=""
              style={{ width: 72, borderRadius: 12, display: 'block' }}
            />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>{known.meaning}</div>
            {/*
              The pronunciation is for a grown-up helping out — it is the one
              thing on this card a nine-year-old cannot read. Hidden from screen
              readers, which say IPA symbols one at a time: gibberish rather
              than a pronunciation.
            */}
            <div className="tiny" style={{ marginTop: 4 }} aria-hidden="true">
              /{known.ipa}/
            </div>
          </div>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="chip"
            type="button"
            onClick={() => {
              void say(known.audioRef, known.word);
            }}
          >
            <span aria-hidden="true">🔊</span> Hear it
          </button>
          <button
            className="chip"
            type="button"
            onClick={() => {
              void say(known.audioRef, known.word, { slow: true });
            }}
          >
            <span aria-hidden="true">🐢</span> Slower
          </button>
        </div>

        {heard !== null && (
          <div className="card" style={{ marginTop: 11, background: 'var(--bg)' }}>
            <div className="kicker">YOU HEARD IT IN</div>
            <div className="tiny" style={{ fontWeight: 700, marginTop: 4 }}>
              “
              {pieces(heard.sentence).map((piece, at) =>
                piece.lookup === known.word.toLowerCase() ? (
                  <b key={at}>{piece.text}</b>
                ) : (
                  piece.text
                ),
              )}
              ” — {heard.where}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Tell the hint card which exercise is on screen.
 *
 * Its own sentences are never offered as *"you heard it in"*: on a replayed
 * lesson that sentence would be the answer to the question being asked.
 */
export function useExerciseOnScreen(exerciseId: string | null): void {
  const { onScreen } = useContext(HintContext);
  useEffect(() => {
    onScreen(exerciseId);
    return () => {
      onScreen(null);
    };
  }, [exerciseId, onScreen]);
}

/**
 * Say one word out loud — the course's own recording of it when the course
 * teaches it, the device's English voice when it does not.
 *
 * For tiles: a word bank is a row of English words a child is choosing
 * between, and hearing each one as it is tapped is how a child who cannot yet
 * read `brother` still builds the sentence. `words` are asked about on the way
 * in, in the same batch as everything else on the screen, so the recording is
 * usually known by the time the first tile is tapped.
 */
export function useSayWord(words: readonly string[]): (word: string) => void {
  const { vocabulary, learn } = useContext(HintContext);
  const line = words.join(' ');
  useEffect(() => {
    learn(line);
  }, [line, learn]);
  return useCallback(
    (word: string) => {
      const known = vocabulary.get(wordsIn(word)[0] ?? lookupForm(word));
      void say(known?.audioRef ?? null, word);
    },
    [vocabulary],
  );
}

/**
 * A line of English, with every word the course teaches made tappable.
 *
 * The spacing and punctuation between words are rendered exactly as they came,
 * so a sentence reads identically whether or not anything in it is tappable —
 * [`pieces`](../../../packages/core/src/words.ts) is what guarantees that, and
 * it is proven there rather than here.
 *
 * A word the course does not teach stays plain text. There is nothing to say
 * about it, and a control that opens an empty card is worse than no control: a
 * child taps it once, gets nothing, and stops trusting the underline.
 */
export function Tappable({ text }: { readonly text: string }) {
  const { vocabulary, learn, ask } = useContext(HintContext);

  useEffect(() => {
    learn(text);
  }, [text, learn]);

  /**
   * The line, as few nodes as it can be.
   *
   * Everything between two askable words is merged into one run of text, and a
   * line with nothing askable in it comes out as the plain string it started
   * as. Emitting a `<span>` per word instead would be a great deal of DOM for
   * no benefit — most words in most sentences are not in the course — and it
   * would break every text match in the app, including the one a screen reader
   * makes when it reads a paragraph.
   */
  const nodes: React.ReactNode[] = [];
  let run = '';
  const flushRun = () => {
    if (run !== '') {
      nodes.push(run);
      run = '';
    }
  };

  for (const [at, piece] of pieces(text).entries()) {
    const known: Known | undefined =
      piece.lookup === null ? undefined : vocabulary.get(piece.lookup);
    if (known === undefined) {
      run += piece.text;
      continue;
    }
    flushRun();
    nodes.push(
      <button
        key={at}
        type="button"
        className="askable"
        // The word is the accessible name; the rest says what tapping does,
        // which is not obvious from the word on its own.
        aria-label={`${piece.text} — what does it mean?`}
        onClick={() => {
          ask(known);
        }}
      >
        {piece.text}
      </button>,
    );
  }
  flushRun();

  return <>{nodes}</>;
}
